'use client';

import { useState, useEffect, useContext } from 'react';
import { Box, useMediaQuery, useTheme, Fade, LinearProgress } from '@mui/material';
import { useRouter, usePathname } from 'next/navigation';
import { ReactReduxContext } from 'react-redux';
import { themeColors } from '../theme/theme';
import { TopNav } from './TopNav';
import { BottomNav } from './BottomNav';
import DesktopFooter from './DesktopFooter';
import DesktopSidebar, { DESKTOP_SIDEBAR_WIDTH, DESKTOP_SIDEBAR_COLLAPSED } from './DesktopSidebar';
import MobileSidebar from './MobileSidebar';
import { logout as logoutAuth } from '../app/redux/slices/authSlice';
import { resetUserProfile } from '../app/redux/slices/userProfileSlice';
import { clearAddressBook } from '../app/redux/slices/addressBookSlice';
import { logout as logoutApi } from '../app/redux/slices/apiSlice';
import { logout as logoutBooking } from '../app/redux/slices/bookingSlice';
import { persistor } from '../app/redux/store';

/**
 * LayoutContainer - Main layout wrapper component
 * Provides responsive layout with sidebar navigation for desktop and drawer for mobile
 */
export function LayoutContainer({
  children,
  sidePanel = null,
  showSidePanel = false,
  hideTopNav = false,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const router = useRouter();
  const pathname = usePathname();

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Redux context
  const reduxContext = useContext(ReactReduxContext);
  let loggedIn = false;
  let user = null;
  let dispatch = () => {};
  let isAuthenticated = false;

  if (reduxContext?.store) {
    const state = reduxContext.store.getState();
    loggedIn = state?.auth?.isLoggedIn;
    isAuthenticated = state?.auth?.isLoggedIn || loggedIn;
    user = state?.userProfile?.data?.user;
    dispatch = reduxContext.store.dispatch;
  }

  const isBusiness = user?.userType?.toLowerCase() === 'business';

  // Don't show sidebar/navigation for non-authenticated users
  const showNavigation = isAuthenticated && user;

  // Active tab from pathname
  const [activeTab, setActiveTab] = useState('home');
  useEffect(() => {
    if (pathname) {
      let path = pathname.split('/')[1] || 'home';
      if (['business-dashboard', 'business-analytics', 'saved-addresses'].includes(path)) {
        path = 'home';
      }
      setActiveTab(path);
      setIsNavigating(false);
    }
  }, [pathname]);

  // Get active key for navigation highlighting (use first path segment and normalize aliases)
  const getActiveKey = () => {
    const seg = pathname?.split('/')?.[1] || 'home';
    // Alias some business routes to 'home' so highlighting is consistent
    if (['business-dashboard', 'business-analytics', 'saved-addresses'].includes(seg)) {
      return 'home';
    }
    return seg;
  };

  // Handle navigation
  const handleNav = (route) => {
    if (route === 'logout') {
      handleLogout();
      return;
    }
    setIsNavigating(true);
    setMobileDrawerOpen(false);

    const routes = {
      home: isBusiness && user?.businessSetupCompleted ? '/business-dashboard' : '/home',
      'business-dashboard': '/business-dashboard',
      'my-pricing': '/my-pricing',
      activeParcels: '/activeParcels',
      history: '/history',
      invoices: '/invoices',
      'saved-addresses': '/saved-addresses',
      'bulk-booking': '/bulk-booking',
      analytics: '/business-analytics',
      profile: '/profile',
      wallet: '/wallet',
      // support: '/support',
      // terms: '/terms',
    };

    if (routes[route]) {
      router.push(routes[route]);
    }
  };

  // Handle logout
  const handleLogout = () => {
    // Clear localStorage FIRST (sync), then hard-navigate to prevent stale UI flash
    if (typeof window !== 'undefined') {
      [
        'auth',
        'api',
        'addressBook',
        'booking',
        'userProfile',
        'shipmentTemplates',
        'persist:root',
      ].forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {}
      });
      // Hard navigate immediately — no React re-renders after this point
      window.location.replace('/login');
      return;
    }
    // SSR fallback (should not reach here in practice)
    router.replace('/login');
  };

  // Navigation loading indicator
  const NavigationLoader = () =>
    isNavigating && (
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
        <LinearProgress
          sx={{
            height: 3,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              bgcolor: isBusiness ? '#f59e0b' : '#000',
            },
          }}
        />
      </Box>
    );

  // Mobile layout
  if (!isDesktop && !isTablet) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: themeColors.backgroundColor }}>
        <NavigationLoader />
        {showNavigation && !hideTopNav && (
          <TopNav
            user={user}
            onMenuClick={() => setMobileDrawerOpen(true)}
            onAvatarClick={() => router.push('/profile')}
            onNotificationsClick={() => router.push('/notifications')}
            onWalletClick={() => router.push('/wallet')}
          />
        )}
        {showNavigation && (
          <MobileSidebar
            user={user}
            isOpen={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            onLogout={handleLogout}
          />
        )}
        <Fade in={!isNavigating} timeout={200}>
          <Box sx={{ pb: showNavigation ? 10 : 0 }}>{children}</Box>
        </Fade>
        {showNavigation && (
          <BottomNav active={activeTab} onNav={handleNav} isLoggedIn={loggedIn} user={user} />
        )}
      </Box>
    );
  }

  // Desktop/Tablet layout
  const mainMargin = showNavigation
    ? sidebarExpanded
      ? DESKTOP_SIDEBAR_WIDTH
      : DESKTOP_SIDEBAR_COLLAPSED
    : 0;
  const showRightPanel = isDesktop && showSidePanel && sidePanel;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: '#f9fafb',
      }}
    >
      <NavigationLoader />
      <Box sx={{ display: 'flex', flex: 1 }}>
        {showNavigation && (
          <DesktopSidebar
            user={user}
            isExpanded={sidebarExpanded}
            onToggleExpanded={() => setSidebarExpanded(!sidebarExpanded)}
            onLogout={handleLogout}
            activeKey={getActiveKey()}
          />
        )}
        <Box
          component="main"
          sx={{
            flex: 1,
            ml: `${mainMargin}px`,
            mr: showRightPanel ? '380px' : 0,
            transition: 'margin 0.25s ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Fade in={!isNavigating} timeout={200}>
            <Box sx={{ flex: 1 }}>{children}</Box>
          </Fade>
          <Box sx={{ ml: `-${mainMargin}px` }}>
            <DesktopFooter />
          </Box>
        </Box>
        {showRightPanel && (
          <Box
            sx={{
              width: 380,
              height: '100vh',
              position: 'fixed',
              right: 0,
              top: 0,
              bgcolor: '#fff',
              borderLeft: '1px solid #e5e7eb',
              overflowY: 'auto',
              zIndex: 1100,
              p: 2,
            }}
          >
            {sidePanel}
          </Box>
        )}
      </Box>
    </Box>
  );
}
